import { useCallback, useEffect, useState } from "react";
import { Plus, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { chip } from "@/components/site/OrderSpecFields";
import { pInput } from "@/components/admin/proposal/ItemRows";
import { ShipmentForm } from "./ShipmentForm";
import { ShipmentCard } from "./ShipmentCard";

const FILTERS = [["andamento", "Em andamento"], ["entregues", "Entregues"], ["todas", "Todas"]];
const delivered = (s) => (s.events || []).at(-1)?.step === "entregue";
const matches = (s, q) => !q || [s.client_name, s.item, s.code, s.tracking_code, s.client_phone].join(" ").toLowerCase().includes(q.toLowerCase());

// Painel de entregas: o lojista cria a entrega, marca as etapas e avisa o cliente.
export const ShipmentsPanel = () => {
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("andamento");
  const [q, setQ] = useState("");
  const [fresh, setFresh] = useState(null);
  const load = useCallback(() => api.get("/admin/shipments").then((r) => setList(Array.isArray(r?.data) ? r.data : [])).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);

  const replace = (doc) => setList((l) => [doc, ...l.filter((x) => x.id !== doc.id)]);
  const create = async (f) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/shipments", f);
      replace(data);
      setFresh(data.id);
      setAdding(false);
      setFilter("andamento");
      toast.success(`Entrega criada · código ${data.code}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (s) => {
    if (!window.confirm(`Apagar a entrega de ${s.client_name}? O cliente não vai mais conseguir acompanhar.`)) return;
    try {
      await api.delete(`/admin/shipments/${s.id}`);
      setList((l) => l.filter((x) => x.id !== s.id));
      toast.success("Entrega apagada");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const shown = list.filter((s) => (filter === "todas" || (filter === "entregues") === delivered(s)) && matches(s, q));

  return (
    <section data-testid="admin-shipments" className="mt-8 rounded-[24px] border border-white/10 bg-[#111113] p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5"><Truck className="h-5 w-5 text-white" /><h2 className="font-display text-xl font-bold">Entregas</h2></div>
        <button type="button" data-testid="shipments-new" onClick={() => setAdding((v) => !v)} className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-black transition-transform hover:scale-[1.02]"><Plus className="h-4 w-4" /> Nova entrega</button>
      </div>
      <p className="mt-2 text-sm text-zinc-400">Crie a entrega, marque cada etapa e mande o link para o cliente acompanhar na página Rastrear pedido. Quem ativar as notificações recebe cada etapa no celular.</p>
      {adding && <div className="mt-5 rounded-2xl border border-white/10 p-4"><ShipmentForm busy={busy} submitLabel="Criar entrega" onSubmit={create} onCancel={() => setAdding(false)} testid="shipment-new-form" /></div>}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        {FILTERS.map(([id, label]) => <button type="button" key={id} data-testid={`shipments-filter-${id}`} onClick={() => setFilter(id)} className={chip(filter === id)}>{label}</button>)}
        <input data-testid="shipments-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente, aparelho ou código" className={`${pInput} sm:ml-auto sm:max-w-xs`} />
      </div>
      {!shown.length && <p data-testid="shipments-empty" className="mt-4 text-sm text-zinc-400">Nenhuma entrega aqui.</p>}
      <ul data-lenis-prevent className="mt-4 max-h-[720px] space-y-2 overflow-y-auto pr-1">
        {shown.map((s) => <ShipmentCard key={s.id} s={s} defaultOpen={s.id === fresh} onChange={replace} onRemove={remove} />)}
      </ul>
    </section>
  );
};
